import { useCallback, useMemo, useState, type Key } from 'react'
import { notification } from 'antd'
import { useSelector } from 'react-redux'
import { selectors as authSelectors } from '@hixme/auth-ui'
import { ROLE_PLATFORM_HIXME_ADMIN } from '@hixme/role-policy'
import { useRouterContext } from 'components/RouterContext'
import { getAdminSettings } from 'enrollment-platform/utils/adminSettings'
import { FINANCE_PATH } from '../../../constants'
import {
  useGenerateAllInvoicesMutation,
  useGenerateInvoiceMutation,
  useGetGeneratedInvoicesQuery,
} from '../../../api/generated-invoice-api-client'
import {
  FundingInvoiceListItemDto,
  GenerateAllResultDto,
  GenerateStatus,
  GetGeneratedInvoicesRequestDto,
} from '../../../api/dto/generated-invoice.dto'
import { useSelectedBillingMonth } from '../useSelectedBillingMonth'

const countByStatus = (result: GenerateAllResultDto) =>
  result.results.reduce(
    (acc, item) => {
      if (item.status === GenerateStatus.Created) acc.created += 1
      if (item.status === GenerateStatus.Regenerated) acc.regenerated += 1
      if (item.status === GenerateStatus.Failed) acc.failed += 1
      return acc
    },
    { created: 0, regenerated: 0, failed: 0 }
  )

const useInvoiceGeneration = () => {
  const { selectedMonth, setSelectedMonth } = useSelectedBillingMonth()
  const { router, location, params } = useRouterContext()

  // The open invoice lives in the URL
  // (/finance/invoice-management/:mode/invoices/:invoiceId) so the drawer state
  // is shareable. `mode` is always 'generation' here, but we read it back so the
  // close path returns to the correct mode.
  const mode = params?.mode === 'delivery' ? 'delivery' : 'generation'
  const drawerInvoiceId = (params?.invoiceId as string | undefined) ?? null

  // Captures the exact row/version that was clicked so the drawer header can show
  // its company/period even for historical versions that aren't in the main list.
  const [clickedInvoice, setClickedInvoice] =
    useState<FundingInvoiceListItemDto | null>(null)
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([])

  const platformRole = useSelector(authSelectors.getRole)
  const isSuperAdmin = platformRole === ROLE_PLATFORM_HIXME_ADMIN

  const { showTestData } = getAdminSettings().finance

  const queryParams = useMemo<GetGeneratedInvoicesRequestDto>(() => {
    const month = selectedMonth.month() + 1
    const year = selectedMonth.year()
    return {
      startMonth: month,
      startYear: year,
      endMonth: month,
      endYear: year,
      excludeTestClients: isSuperAdmin && !showTestData ? true : undefined,
    }
  }, [isSuperAdmin, selectedMonth, showTestData])

  const invoicesQuery = useGetGeneratedInvoicesQuery(queryParams)

  const generateAllMutation = useGenerateAllInvoicesMutation({
    onSuccess: (result) => {
      const stats = countByStatus(result)
      const parts = [
        `${stats.created} created`,
        `${stats.regenerated} regenerated`,
      ]
      if (stats.failed > 0) parts.push(`${stats.failed} failed`)

      if (stats.failed > 0) {
        notification.warning({
          message: `Generation complete (${parts.join(', ')})`,
        })
      } else {
        notification.success({
          message: `Generation complete (${parts.join(', ')})`,
        })
      }
    },
    onError: (error) => {
      notification.error({
        message: 'Failed to generate invoices',
        description: error.message,
      })
    },
  })

  const generateMutation = useGenerateInvoiceMutation({
    onSuccess: (result) => {
      if (result.status === GenerateStatus.Failed) {
        notification.error({
          message: 'Regeneration failed',
          description: result.error ?? 'Unknown error',
        })
        return
      }
      notification.success({
        message:
          result.status === GenerateStatus.Regenerated
            ? 'Invoice regenerated'
            : 'Invoice generated',
      })
    },
    onError: (error) => {
      notification.error({
        message: 'Failed to regenerate invoice',
        description: error.message,
      })
    },
  })

  const handleGenerateAll = useCallback(() => {
    generateAllMutation.mutate({
      billingPeriodStart: selectedMonth.startOf('month').format('YYYY-MM-DD'),
    })
  }, [generateAllMutation, selectedMonth])

  const handleRegenerate = useCallback(
    (invoice: FundingInvoiceListItemDto) => {
      if (!invoice.clientId) {
        notification.warning({
          message: 'Client is required to regenerate this invoice',
        })
        return
      }
      generateMutation.mutate({
        clientPublicKey: invoice.clientId,
        billingPeriodStart: invoice.billingPeriodStart,
      })
    },
    [generateMutation]
  )

  const handleOpenDrawer = useCallback(
    (invoice: FundingInvoiceListItemDto) => {
      setClickedInvoice(invoice)
      router?.push({
        pathname: `${FINANCE_PATH}/invoice-management/${mode}/invoices/${encodeURIComponent(
          invoice.id
        )}`,
        query: location?.query ?? {},
      })
    },
    [router, mode, location?.query]
  )

  const handleCloseDrawer = useCallback(() => {
    setClickedInvoice(null)
    router?.push({
      pathname: `${FINANCE_PATH}/invoice-management/${mode}`,
      query: location?.query ?? {},
    })
  }, [router, mode, location?.query])

  const rows = invoicesQuery.data?.data ?? []

  // Prefer the clicked row (covers historical versions); fall back to matching a
  // row from the current list when arriving via a shared URL.
  const selectedInvoice = useMemo<FundingInvoiceListItemDto | null>(() => {
    if (!drawerInvoiceId) return null
    if (clickedInvoice?.id === drawerInvoiceId) return clickedInvoice
    return rows.find((row) => row.id === drawerInvoiceId) ?? null
  }, [drawerInvoiceId, clickedInvoice, rows])

  return {
    selectedMonth,
    setSelectedMonth,
    rows,
    total: invoicesQuery.data?.total ?? 0,
    isLoading: invoicesQuery.isLoading,
    isError: invoicesQuery.isError,

    expandedRowKeys,
    setExpandedRowKeys,

    drawerInvoiceId,
    selectedInvoice,
    handleOpenDrawer,
    handleCloseDrawer,

    handleGenerateAll,
    handleRegenerate,
    isGeneratingAll: generateAllMutation.isPending,
    isRegenerating: generateMutation.isPending,
    regeneratingInvoiceKey: generateMutation.variables
      ? `${generateMutation.variables.clientPublicKey}:${generateMutation.variables.billingPeriodStart}`
      : undefined,
  }
}

export default useInvoiceGeneration
